import React from "react";

import {
  FaArrowDown,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaPause,
  FaPlay,
  FaTrophy,
} from "react-icons/fa";

import LandingButton from "../../../components/Button/LandingButton";

import useTournamentHeroSlider from "../hooks/useTournamentHeroSlider";

import {
  TOURNAMENT_HERO_BADGES,
  TOURNAMENT_HERO_SLIDES,
  TOURNAMENT_HERO_STATS,
} from "../data/tournamentProductData";

import {
  cx,
  scrollToTournamentSection,
} from "../utils/tournamentProductUtils";

function TournamentStatCard({
  icon: Icon,
  label,
  value,
  description,
}) {
  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-slate-900/90">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 ring-1 ring-orange-100 dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-400/20">
          <Icon className="h-4 w-4" />
        </div>

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
            {label}
          </p>

          <p className="mt-1 font-bold text-slate-950 dark:text-white">
            {value}
          </p>

          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {description}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function TournamentHero({
  onDemoClick,
}) {
  const {
    currentIndex,
    currentSlide,
    paused,
    setPaused,
    goTo,
    next,
    previous,
  } = useTournamentHeroSlider({
    slides:
      TOURNAMENT_HERO_SLIDES,

    interval: 6500,
  });

  if (!currentSlide) {
    return null;
  }

  return (
    <section className="relative isolate overflow-hidden border-b border-slate-200 bg-[#fff8f1] dark:border-white/10 dark:bg-slate-950">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(249,115,22,0.16),transparent_28%),radial-gradient(circle_at_85%_20%,rgba(37,99,235,0.12),transparent_30%),radial-gradient(circle_at_50%_100%,rgba(251,191,36,0.12),transparent_35%)]" />

      <div className="pointer-events-none absolute -left-24 top-40 h-80 w-80 rounded-full bg-orange-300/20 blur-3xl dark:bg-orange-500/10" />

      <div className="pointer-events-none absolute -right-24 top-16 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl dark:bg-blue-500/10" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 lg:pb-24 lg:pt-32">
        <div className="grid items-center gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          {/* Présentation */}
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-orange-700 shadow-sm dark:border-orange-400/20 dark:bg-slate-900 dark:text-orange-300">
              <FaTrophy className="h-3.5 w-3.5" />

              MonAppliTournoi
            </div>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl dark:text-white">
              Organisez vos tournois de judo
              <span className="block bg-gradient-to-r from-orange-500 via-amber-500 to-blue-600 bg-clip-text text-transparent">
                avec une seule application
              </span>
            </h1>

            <p className="mt-6 text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              MonAppliTournoi accompagne les clubs et les
              organisateurs de la préparation de la compétition
              jusqu’aux résultats publics : inscriptions, pesées,
              poules, tableaux, combats, scoreboards et podiums.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <LandingButton
                variant="primary"
                onClick={onDemoClick}
              >
                Voir le site en ligne
                <FaExternalLinkAlt className="ml-2 text-xs" />
              </LandingButton>

              <LandingButton
                variant="secondary"
                onClick={() =>
                  scrollToTournamentSection(
                    "tournoi-about",
                  )
                }
              >
                Découvrir les fonctionnalités
              </LandingButton>

              <LandingButton
                variant="secondary"
                onClick={() =>
                  scrollToTournamentSection(
                    "tournoi-contact",
                  )
                }
              >
                Demander une démonstration
              </LandingButton>
            </div>

            <div className="mt-7 flex flex-wrap gap-2">
              {TOURNAMENT_HERO_BADGES.map(
                (badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
                  >
                    <FaCheckCircle className="text-emerald-500" />

                    {badge}
                  </span>
                ),
              )}
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {TOURNAMENT_HERO_STATS.map(
                (stat) => (
                  <TournamentStatCard
                    key={stat.label}
                    {...stat}
                  />
                ),
              )}
            </div>
          </div>

          {/* Aperçu produit */}
          <div
            className="relative"
            onMouseEnter={() =>
              setPaused(true)
            }
            onMouseLeave={() =>
              setPaused(false)
            }
          >
            <div className="absolute -left-7 top-12 hidden rounded-3xl border border-white/80 bg-white/90 p-4 shadow-xl backdrop-blur dark:border-white/10 dark:bg-slate-900/90 lg:block">
              <div className="text-xs font-bold uppercase tracking-[0.12em] text-orange-500">
                Tournoi complet
              </div>

              <div className="mt-1 font-bold text-slate-950 dark:text-white">
                Du contrôle des pesées
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400">
                jusqu’aux podiums
              </div>
            </div>

            <div className="relative overflow-hidden rounded-[34px] border border-slate-200 bg-white shadow-[0_30px_90px_-25px_rgba(15,23,42,0.30)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_30px_90px_-25px_rgba(0,0,0,0.65)]">
              {/* Barre navigateur */}
              <div className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-white/10">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full bg-red-400" />
                  <span className="h-3 w-3 rounded-full bg-amber-400" />
                  <span className="h-3 w-3 rounded-full bg-emerald-400" />
                </div>

                <div className="truncate rounded-full bg-slate-100 px-4 py-1.5 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  Aperçu MonAppliTournoi
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="relative overflow-hidden rounded-[26px] border border-slate-200 bg-slate-950 dark:border-white/10">
                  <div className="aspect-[4/3] sm:aspect-[16/10]">
                    <img
                      key={currentSlide.src}
                      src={currentSlide.src}
                      alt={currentSlide.alt}
                      loading={
                        currentIndex === 0
                          ? "eager"
                          : "lazy"
                      }
                      className="h-full w-full object-cover transition duration-700"
                    />
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />

                  <div className="absolute inset-x-0 bottom-0 p-5 text-white sm:p-6">
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-orange-300">
                      {currentSlide.eyebrow}
                    </div>

                    <h2 className="mt-2 text-2xl font-bold sm:text-3xl">
                      {currentSlide.title}

                      <span className="block text-orange-300">
                        {currentSlide.highlight}
                      </span>
                    </h2>

                    <p className="mt-3 max-w-xl text-sm leading-6 text-white/75">
                      {currentSlide.description}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={previous}
                    className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/35 text-white backdrop-blur transition hover:bg-orange-500"
                    aria-label="Image précédente"
                  >
                    <FaArrowLeft />
                  </button>

                  <button
                    type="button"
                    onClick={next}
                    className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl border border-white/15 bg-slate-950/35 text-white backdrop-blur transition hover:bg-orange-500"
                    aria-label="Image suivante"
                  >
                    <FaArrowRight />
                  </button>
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    {TOURNAMENT_HERO_SLIDES.map(
                      (slide, index) => (
                        <button
                          key={slide.id}
                          type="button"
                          onClick={() =>
                            goTo(index)
                          }
                          className={cx(
                            "h-2.5 rounded-full transition-all",
                            index ===
                              currentIndex
                              ? "w-9 bg-orange-500"
                              : "w-2.5 bg-slate-300 hover:bg-slate-400 dark:bg-slate-700 dark:hover:bg-slate-600",
                          )}
                          aria-label={`Afficher le visuel ${index + 1}`}
                        />
                      ),
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setPaused(
                        (current) =>
                          !current,
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 transition hover:border-orange-200 hover:text-orange-600 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300 dark:hover:border-orange-400/30 dark:hover:text-orange-300"
                  >
                    {paused ? (
                      <>
                        <FaPlay />
                        Lecture
                      </>
                    ) : (
                      <>
                        <FaPause />
                        Pause
                      </>
                    )}
                  </button>
                </div>

                <div className="mt-5 rounded-[26px] bg-gradient-to-br from-orange-500 via-orange-500 to-blue-600 p-5 text-white">
                  <p className="text-sm font-medium text-white/75">
                    Une organisation pensée pour le terrain
                  </p>

                  <p className="mt-2 text-2xl font-bold sm:text-3xl">
                    Moins de manipulation.
                    <br />
                    Plus de fluidité le jour J.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                scrollToTournamentSection(
                  "tournoi-about",
                )
              }
              className="mx-auto mt-6 flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-lg transition hover:-translate-y-1 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-600 dark:border-white/10 dark:bg-slate-900 dark:text-slate-300 dark:hover:border-orange-400/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-300"
              aria-label="Découvrir les fonctionnalités"
            >
              <FaArrowDown />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}